import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, NavLink } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useBranding } from '@/contexts/BrandingContext';
import { useLanguage } from '@/i18n/LanguageContext';

// UI Components
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Building2, 
  FileText, 
  LogOut, 
  Menu,
  X,
  Handshake,
  Settings
} from 'lucide-react';

// Partner Components
import PartnerDashboardHome from '@/components/partner/PartnerDashboardHome';
import PartnerCompaniesPage from '@/components/partner/PartnerCompaniesPage';
import PartnerReportsPage from '@/components/partner/PartnerReportsPage';
import LanguageSelector from '@/components/LanguageSelector';
import ThemeToggle from '@/components/ThemeToggle';

function PartnerDashboard() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [partner, setPartner] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      if (userRes.data.role !== 'partner') {
        navigate('/partner');
        return;
      }
      setUser(userRes.data);
      
      const partnerRes = await axios.get(`${API}/partner/profile`, { withCredentials: true });
      setPartner(partnerRes.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      navigate('/partner');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    navigate('/partner');
  };

  const navItems = [
    { path: '/partner/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/partner/companies', icon: Building2, label: t('nav.companies') },
    { path: '/partner/reports', icon: FileText, label: t('nav.reports') },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-40
        w-64 bg-white border-r border-gray-200
        transform transition-transform duration-200 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        ${sidebarOpen ? 'lg:w-64' : 'lg:w-20'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-center h-16 border-b border-gray-200 px-4">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-8 object-contain" />
            ) : (
              <div className="flex items-center gap-2">
                <Handshake className="w-8 h-8 text-purple-600" />
                {sidebarOpen && <span className="font-bold text-purple-600">Partner</span>}
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => `
                      flex items-center gap-3 px-4 py-3 rounded-lg
                      transition-colors duration-200
                      ${isActive 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'text-gray-600 hover:bg-gray-100'
                      }
                    `}
                  >
                    <item.icon className="w-5 h-5" />
                    {sidebarOpen && <span className="font-medium">{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* User Info */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-purple-100 text-purple-600">
                  {user?.name?.charAt(0) || 'P'}
                </AvatarFallback>
              </Avatar>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className={`
        transition-all duration-200
        ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
        min-h-screen
      `}>
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-6">
          <div className="flex items-center gap-4 ml-12 lg:ml-0">
            <h1 className="text-lg font-semibold text-gray-900">Partner Portal</h1>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-purple-100 text-purple-600">
                      {user?.name?.charAt(0) || 'P'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t('common.details')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('nav.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <Routes>
          <Route path="dashboard" element={<PartnerDashboardHome partner={partner} />} />
          <Route path="companies" element={<PartnerCompaniesPage partner={partner} />} />
          <Route path="reports" element={<PartnerReportsPage partner={partner} />} />
          <Route path="*" element={<PartnerDashboardHome partner={partner} />} />
        </Routes>
      </main>
    </div>
  );
}

export default PartnerDashboard;
